import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Zap, LayoutGrid } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import SmartCard from "@/components/wingman/SmartCard";

// All card types with varied content and urgency for UX experimentation
const TEST_CARDS = [
  {
    card_id: "test_fact_check",
    card_type: "fact_check",
    urgency: "immediate",
    pinned: false,
    used: false,
    content: {
      claim: "SaaS market grew 18% year-over-year according to Gartner",
      verdict: "partially_accurate",
      explanation: "Gartner's latest report shows SaaS market grew 16.7% YoY, not 18%. Close but worth noting the precise figure.",
      sources: ["https://example.com/gartner-report"],
      grounded: true,
    },
  },
  {
    card_id: "test_fact_check_accurate",
    card_type: "fact_check",
    urgency: "when_ready",
    pinned: false,
    used: false,
    content: {
      claim: "Our retention rate is 94%",
      verdict: "accurate",
      explanation: "Confirmed from internal metrics. Best-in-class for our segment.",
      sources: [],
      grounded: true,
    },
  },
  {
    card_id: "test_follow_up",
    card_type: "follow_up",
    urgency: "immediate",
    pinned: true,
    used: false,
    content: {
      question: "What specific healthcare sub-verticals are you considering? Hospitals, clinics, or health-tech startups?",
      trigger: "target the healthcare vertical",
      rationale: "Healthcare is broad. Narrowing the focus will help prioritize the right ICP.",
      priority: "high",
    },
  },
  {
    card_id: "test_follow_up_medium",
    card_type: "follow_up",
    urgency: "when_ready",
    pinned: false,
    used: false,
    content: {
      question: "Have you validated pricing with any beta customers yet?",
      trigger: "pricing discussion",
      rationale: "Early validation reduces risk before full launch.",
      priority: "medium",
    },
  },
  {
    card_id: "test_defense",
    card_type: "defense",
    urgency: "immediate",
    pinned: false,
    used: false,
    content: {
      challenge: "Acme Corp just raised 50M, so they'll be aggressive on pricing",
      counter_points: [
        "Funding doesn't guarantee product quality — many well-funded competitors burn cash on growth, not product.",
        "Your 94% retention rate shows customers stay because the product delivers, not because of pricing.",
      ],
      reframe: "Their fundraise validates the market, not their product. Our retention proves we've already won on value.",
    },
  },
  {
    card_id: "test_example",
    card_type: "example",
    urgency: "when_ready",
    pinned: false,
    used: false,
    content: {
      concept: "Value-based selling",
      examples: [
        {
          example: "Instead of 'We have 50 integrations,' say 'You'll save 8 hours/week on manual data entry.'",
          how_to_use: "Lead with outcome, not feature.",
        },
        {
          example: "Anchor on ROI: 'Customers typically see payback in 3 months.'",
          how_to_use: "Quantify the benefit when possible.",
        },
      ],
    },
  },
  {
    card_id: "test_nudge",
    card_type: "nudge",
    urgency: "immediate",
    pinned: false,
    used: false,
    content: {
      uncovered_item: "Budget allocation",
      transition: "We've covered the timeline. Should we discuss how to allocate budget across the initiatives?",
      reason: "Budget wasn't in the agenda but came up in context.",
    },
  },
  {
    card_id: "test_nudge_info",
    card_type: "nudge",
    urgency: "info",
    pinned: false,
    used: false,
    content: {
      uncovered_item: "Competitor comparison",
      transition: "You might want to mention how we compare to Acme on support SLAs.",
      reason: "Support was a concern in the last call.",
    },
  },
  {
    card_id: "test_action_item",
    card_type: "action_item",
    urgency: "when_ready",
    pinned: false,
    used: false,
    content: {
      action: "Create healthcare GTM plan draft",
      owner: "Mike (Sales)",
      deadline: "Friday",
      quote: "I'll have a draft ready by Friday.",
    },
  },
  {
    card_id: "test_action_item_used",
    card_type: "action_item",
    urgency: "background",
    pinned: false,
    used: true,
    content: {
      action: "Send pricing deck to procurement",
      owner: "Sarah (PM)",
      deadline: "EOD",
      quote: "I'll send it over before we wrap.",
    },
  },
  {
    card_id: "test_objection",
    card_type: "objection",
    urgency: "immediate",
    pinned: false,
    used: false,
    content: {
      tension_source: "Budget constraints for Q2",
      their_concern: "Finance is pushing back on new tool spend.",
      suggested_response: "We could start with a pilot for one team — lower commitment, faster time-to-value.",
      compromise: "3-month pilot with option to expand.",
    },
  },
  {
    card_id: "test_pace_alert",
    card_type: "pace_alert",
    urgency: "immediate",
    pinned: false,
    used: false,
    content: {
      message: "You're at 75% of allocated time for this agenda item.",
      elapsed_minutes: 9,
      total_allocated_minutes: 12,
      uncovered_items: ["Budget discussion", "Next steps"],
    },
  },
  {
    card_id: "test_pace_alert_over",
    card_type: "pace_alert",
    urgency: "immediate",
    pinned: false,
    used: false,
    content: {
      message: "Over time on 'Competitor positioning' — consider wrapping and parking remaining points.",
      elapsed_minutes: 18,
      total_allocated_minutes: 15,
      uncovered_items: ["Pricing comparison slide"],
    },
  },
];

export default function CardTestPage() {
  const navigate = useNavigate();
  const [cards, setCards] = useState(TEST_CARDS);

  const dismissCard = (cardId) => setCards((prev) => prev.filter((c) => c.card_id !== cardId));
  const pinCard = (cardId) => setCards((prev) => prev.map((c) => (c.card_id === cardId ? { ...c, pinned: !c.pinned } : c)));
  const markUsed = (cardId) => setCards((prev) => prev.map((c) => (c.card_id === cardId ? { ...c, used: true } : c)));

  const resetCards = () => setCards(TEST_CARDS);

  return (
    <div className="min-h-screen mesh-bg flex flex-col">
      <header className="sticky top-0 z-50 border-b border-gray-100 bg-white/95 backdrop-blur-sm px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between max-w-[1400px] mx-auto">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex items-center gap-2">
              <LayoutGrid className="w-5 h-5 text-indigo-500" />
              <div>
                <h1 className="font-semibold text-gray-900 text-lg">Card UX Test</h1>
                <p className="text-xs text-gray-500">All card types stacked — experiment with layout and styling</p>
              </div>
            </div>
          </div>
          <button
            onClick={resetCards}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
          >
            Reset cards
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-hidden p-4 sm:p-6">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-4 h-4 text-indigo-500" />
            <span className="text-sm font-medium text-gray-600">Smart Cards</span>
            <span className="text-xs text-gray-400 ml-auto">{cards.length} cards</span>
          </div>

          <div className="flex flex-col gap-5 overflow-y-auto custom-scrollbar max-h-[calc(100vh-140px)] pr-2 max-w-3xl py-2">
            <AnimatePresence mode="popLayout">
              {cards.map((card) => (
                <SmartCard
                  key={card.card_id}
                  card={card}
                  onDismiss={dismissCard}
                  onPin={pinCard}
                  onUsed={markUsed}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  );
}
